/**
 * Zone 2 — 6 social activity pills.
 * A compact single-row display of up to N social metrics.
 */

type PillItem = {
  readonly label: string;
  readonly value: string | number;
  readonly suffix?: string;
  readonly onClick?: () => void;
};

type PillRowProps = {
  readonly items: ReadonlyArray<PillItem>;
};

function Pill({ item }: { readonly item: PillItem }): React.JSX.Element {
  const isClickable = typeof item.onClick === "function";

  function handleKeyDown(e: React.KeyboardEvent): void {
    if ((e.key === "Enter" || e.key === " ") && item.onClick) {
      item.onClick();
    }
  }

  return (
    <span
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? item.onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      style={{
        ...s.pill,
        cursor: isClickable ? "pointer" : "default",
      }}
    >
      <span style={s.label}>{item.label}</span>
      <span style={s.value}>{item.value}</span>
      {item.suffix !== undefined && <span style={s.suffix}>{item.suffix}</span>}
    </span>
  );
}

export function PillRow({ items }: PillRowProps): React.JSX.Element {
  return (
    <div style={s.container}>
      {items.map((item) => (
        <Pill key={item.label} item={item} />
      ))}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "12px 0",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 20,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    fontSize: 13,
  },
  label: {
    color: "var(--color-text-secondary)",
    fontWeight: 400,
  },
  value: {
    fontWeight: 700,
    fontFamily: "var(--font-mono)",
    color: "var(--color-text)",
  },
  suffix: {
    fontWeight: 700,
    fontFamily: "var(--font-mono)",
    color: "var(--color-text-secondary)",
  },
};
